import { useState } from "react";
import { toast } from "sonner";
import { Search, Check } from "lucide-react";
import api, { formatApiError, currency } from "@/lib/api";
import { PublicNav, Footer } from "@/components/PublicLayout";

const STATUS_COLORS = {
  Estimate: "text-zinc-300 bg-zinc-700/40",
  Approved: "text-green-400 bg-green-500/15",
  "In Progress": "text-yellow-400 bg-yellow-500/15",
  Completed: "text-blue-400 bg-blue-500/15",
  Declined: "text-red-400 bg-red-500/15",
};

function EstimateCard({ build, onApproved }) {
  const editable = build.status === "Estimate" || build.status === "Approved";
  const [selected, setSelected] = useState(
    () => new Set(build.line_items.filter((i) => i.approved !== false).map((i) => i.id))
  );
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    if (!editable) return;
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const total = build.line_items
    .filter((i) => selected.has(i.id))
    .reduce((s, i) => s + i.qty * i.unit_price, 0);

  const approve = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/portal/builds/${build.id}/approve`, { approved_item_ids: [...selected] });
      toast.success("Estimate approved! Our team has been notified.");
      onApproved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const decline = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/portal/builds/${build.id}/decline`);
      toast("Estimate declined. Our team has been notified.");
      onApproved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-zinc-800 bg-zinc-950 mb-6" data-testid={`portal-build-${build.build_number}`}>
      <div className="flex items-center justify-between p-5 border-b border-zinc-800">
        <div>
          <p className="font-display text-lg font-bold">{build.build_number}</p>
          <p className="text-zinc-400 text-sm">{build.vehicle.year} {build.vehicle.make} {build.vehicle.model} · {build.vehicle.plate}</p>
        </div>
        <span className={`text-xs font-bold uppercase px-3 py-1 ${STATUS_COLORS[build.status]}`}>{build.status}</span>
      </div>
      <div className="divide-y divide-zinc-800">
        {build.line_items.map((i) => (
          <button key={i.id} onClick={() => toggle(i.id)} disabled={!editable}
            data-testid={`portal-lineitem-${i.id}`}
            className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors duration-150 ${editable ? "hover:bg-zinc-900" : ""} ${selected.has(i.id) ? "" : "opacity-50"}`}>
            <span className={`w-5 h-5 flex-shrink-0 flex items-center justify-center border ${selected.has(i.id) ? "bg-yellow-500 border-yellow-500 text-black" : "border-zinc-600"}`}>
              {selected.has(i.id) && <Check size={13} />}
            </span>
            <span className="flex-1">
              <span className="text-sm text-zinc-200">{i.description}</span>
              <span className="text-xs text-zinc-500 ml-2 uppercase">{i.type}</span>
            </span>
            <span className="text-sm text-zinc-400">{i.qty} × {currency(i.unit_price)}</span>
            <span className="text-sm font-semibold w-24 text-right">{currency(i.qty * i.unit_price)}</span>
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between p-5 border-t border-zinc-800">
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-500">Selected total</p>
          <p className="font-display text-2xl font-black text-yellow-500" data-testid={`portal-total-${build.build_number}`}>{currency(total)}</p>
        </div>
        {editable && (
          <div className="flex gap-3">
            <button onClick={decline} disabled={saving} data-testid={`portal-decline-${build.build_number}`}
              className="border border-zinc-700 text-zinc-300 font-bold uppercase px-6 py-3 hover:border-red-500 hover:text-red-400 transition-colors duration-150 disabled:opacity-60">
              Decline
            </button>
            <button onClick={approve} disabled={saving} data-testid={`portal-approve-${build.build_number}`}
              className="bg-yellow-500 text-black font-bold uppercase px-6 py-3 hover:bg-yellow-300 transition-colors duration-150 disabled:opacity-60">
              {saving ? "Saving…" : "Approve Selected"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomerPortal() {
  const [email, setEmail] = useState("");
  const [builds, setBuilds] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.get(`/portal/builds`, { params: { email } });
      setBuilds(data);
      if (data.length === 0) toast.info("No estimates found for that email.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const onApproved = (updated) =>
    setBuilds((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));

  return (
    <div className="bg-[#09090b] min-h-screen">
      <PublicNav />
      <div className="max-w-3xl mx-auto px-6 py-20">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-yellow-500 mb-2">Customer Portal</p>
        <h1 className="font-display text-4xl lg:text-5xl font-black uppercase tracking-tighter mb-3">Review & Approve</h1>
        <p className="text-zinc-400 mb-8">Enter the email on your account to view estimates and approve the work you want done.</p>

        <form onSubmit={lookup} className="flex gap-3 mb-10">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" data-testid="portal-email-input"
            className="flex-1 bg-zinc-900 border border-zinc-800 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-shadow duration-150" />
          <button disabled={loading} data-testid="portal-lookup-btn"
            className="bg-yellow-500 text-black font-bold uppercase px-6 py-3 flex items-center gap-2 hover:bg-yellow-300 transition-colors duration-150 disabled:opacity-60">
            <Search size={18} /> {loading ? "…" : "Look up"}
          </button>
        </form>

        {builds && builds.length === 0 && (
          <p className="text-zinc-500 text-center py-10" data-testid="portal-empty">No estimates found. Try a demo email like fleet@sunriselogistics.com</p>
        )}
        {builds && builds.map((b) => <EstimateCard key={b.id} build={b} onApproved={onApproved} />)}
      </div>
      <Footer />
    </div>
  );
}
